import { useState, useEffect, useCallback, useRef } from 'react';
import { Loader2, RefreshCw } from 'lucide-react';
import axios from 'axios';
import { Button } from '../ui/button';
import { useAuth } from '../../context/AuthContext';
import { useLanguage } from '../../context/LanguageContext';

const API = `${(process.env.REACT_APP_BACKEND_URL || '').replace(/\/+$/, '')}/api`;
const WAIT_FOR_DOCS_MS = 60000;
const SKIP_AFTER_MS = 8000;      // ab hier kann der Nutzer das Warten auf Dokumente abbrechen

/**
 * Letzter Onboarding-Schritt für Personen-Projekte, ohne Eingaben.
 * Die KI baut aus Lebenslauf, Freitext und Arbeitsumfeld (Website, Brand DNA, Wissen) das Personenprofil
 * (Ziel, Zielgruppe, Positionierung, Themen) und speichert es. Der Nutzer sieht es erst später in der
 * Strategie-Ansicht und kann es dort bearbeiten.
 */
const StepPersonFinalize = ({ projectId, onComplete }) => {
  const { getAuthHeader } = useAuth();
  const { lang } = useLanguage();
  const T = (de, en) => (lang === 'de' ? de : en);

  const [phase, setPhase] = useState('docs');          // docs → draft → error
  const [error, setError] = useState('');
  const started = useRef(false);
  const skipWait = useRef(false);
  const [canSkip, setCanSkip] = useState(false);

  // Hochgeladene Wissens-Dokumente werden im Hintergrund eingelesen: kurz darauf warten, damit sie ins Profil einfließen.
  const waitForDocs = useCallback(async () => {
    const until = Date.now() + WAIT_FOR_DOCS_MS;
    while (Date.now() < until) {
      try {
        const { data } = await axios.get(`${API}/knowledge`, { headers: getAuthHeader() });
        if (!(data.documents || []).some(d => d.status === 'reading' || d.status === 'summarizing')) return;
      } catch { return; }
      if (skipWait.current) return;
      await new Promise(r => setTimeout(r, 2500));
    }
  }, []); // eslint-disable-line

  const run = useCallback(async () => {
    setError('');
    setPhase('docs');
    setCanSkip(false);
    skipWait.current = false;
    const skipTimer = setTimeout(() => setCanSkip(true), SKIP_AFTER_MS);
    await waitForDocs();
    clearTimeout(skipTimer);
    setPhase('draft');
    try {
      const { data } = await axios.post(`${API}/projects/personal-brand/draft`,
        { project_id: projectId, language: lang }, { headers: getAuthHeader(), timeout: 90000 });
      await axios.put(`${API}/projects/${projectId}/personal-brand`,
        { personal_brand: data.draft, language: lang }, { headers: getAuthHeader() });
      onComplete();
    } catch (err) {
      setError(err.response?.data?.detail || T('Das Profil konnte nicht erstellt werden.', 'The profile could not be created.'));
      setPhase('error');
    }
  }, [projectId, lang]); // eslint-disable-line

  useEffect(() => {
    if (started.current) return;
    started.current = true;
    run();
  }, [run]);

  // Ohne Entwurf wenigstens die Angaben der Person sichern, damit Ideen und Strategie funktionieren
  const skipWithBasics = async () => {
    try {
      const { data } = await axios.get(`${API}/projects/${projectId}/personal-brand`, { headers: getAuthHeader() });
      const basics = data.personal_basics || {};
      if (Object.keys(basics).length) {
        await axios.put(`${API}/projects/${projectId}/personal-brand`,
          { personal_brand: basics, language: lang }, { headers: getAuthHeader() });
      }
    } catch { /* weiter ohne */ }
    onComplete();
  };

  return (
    <div data-testid="step-person-finalize">
      {phase !== 'error' ? (
        <div className="text-center py-14" data-testid="pb-generating">
          <Loader2 className="w-8 h-8 animate-spin text-[#00D9C8] mx-auto mb-4" />
          <p className="text-white font-medium">{T('Wir bauen dein Profil …', 'Building your profile …')}</p>
          <p className="text-sm text-[#A0AEC0] mt-1">
            {phase === 'docs'
              ? T('Deine Dokumente werden gelesen.', 'Reading your documents.')
              : T('Aus deinem Werdegang entstehen Themen und Positionierung.', 'Topics and positioning are drawn from your background.')}
          </p>
          {phase === 'docs' && canSkip && (
            <button type="button" onClick={() => { skipWait.current = true; }} className="mt-4 text-sm text-[#00D9C8] hover:underline" data-testid="pb-dont-wait">
              {T('Nicht warten. Die Dokumente stehen danach trotzdem im Wissen bereit.', 'Don’t wait. The documents will still be available in Knowledge.')}
            </button>
          )}
        </div>
      ) : (
        <div className="space-y-3" data-testid="pb-draft-error">
          <p className="text-red-400 text-sm">{error}</p>
          <Button onClick={run} className="w-full bg-[#00D9C8] hover:bg-[#00C4B4] text-[#0A1628]"><RefreshCw className="w-4 h-4 mr-2" />{T('Nochmal versuchen', 'Try again')}</Button>
          <button type="button" onClick={skipWithBasics} className="w-full text-sm text-[#5A6C7D] hover:text-white" data-testid="pb-skip">{T('Überspringen, den Entwurf später in der Strategie erstellen', 'Skip, create the draft later in Strategy')}</button>
        </div>
      )}
    </div>
  );
};

export default StepPersonFinalize;
